import React, { useState, useEffect } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../../lib/supabase';
import { ClipboardList, CheckCircle, XCircle, Clock } from 'lucide-react';
import type { BetaApplication } from '../../types';
import ApplicationModal from './modals/ApplicationModal';

interface ApplicationManagerProps {
  user: User;
}

export default function ApplicationManager({ user }: ApplicationManagerProps) {
  const [applications, setApplications] = useState<BetaApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [selectedApplication, setSelectedApplication] = useState<BetaApplication | null>(null);

  useEffect(() => {
    loadApplications();
  }, [user.id]);

  const loadApplications = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('beta_reader_applications')
        .select(`
          *,
          book_ideas (
            title,
            description
          ),
          author:profiles!beta_reader_applications_author_id_fkey (
            full_name
          ),
          profiles:reader_id (
            username,
            full_name
          )
        `)
        .eq('author_id', user.id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setApplications(data || []);
    } catch (error) {
      console.error('Error loading applications:', error);
      setError(error instanceof Error ? error.message : 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const updateApplicationStatus = async (applicationId: string, newStatus: 'approved' | 'rejected') => {
    try {
      setUpdatingId(applicationId);
      setError(null);

      const { error: updateError } = await supabase
        .from('beta_reader_applications')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', applicationId);

      if (updateError) throw updateError;

      setApplications(prev =>
        prev.map(app =>
          app.id === applicationId
            ? { ...app, status: newStatus }
            : app
        )
      );
      
      // Close the modal after updating
      setSelectedApplication(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update application status');
    } finally {
      setUpdatingId(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-6">
        <ClipboardList className="w-5 h-5 text-violet-500" />
        Beta Reader Applications
      </h2>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      <div className="divide-y">
        {applications.map(application => (
          <div
            key={application.id}
            className="py-4 first:pt-0 last:pb-0 hover:bg-gray-50 cursor-pointer"
            onClick={() => setSelectedApplication(application)}
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-medium text-gray-900 mb-1">
                  {application.book_ideas.title}
                </h3>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span>
                    From: {application.profiles.full_name || application.profiles.username}
                  </span>
                  <span>
                    Applied {new Date(application.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm ${
                application.status === 'approved'
                  ? 'bg-green-100 text-green-800'
                  : application.status === 'rejected'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {application.status === 'approved' ? (
                  <CheckCircle className="w-4 h-4" />
                ) : application.status === 'rejected' ? (
                  <XCircle className="w-4 h-4" />
                ) : (
                  <Clock className="w-4 h-4" />
                )}
                <span className="font-medium">
                  {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                </span>
              </div>
            </div>
          </div>
        ))}

        {applications.length === 0 && (
          <div className="text-center py-8">
            <ClipboardList className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Applications Yet</h3>
            <p className="text-gray-600">
              When readers apply to beta read your books, they'll appear here.
            </p>
          </div>
        )}
      </div>

      {/* Application Modal */}
      {selectedApplication && (
        <ApplicationModal
          isOpen={true}
          onClose={() => setSelectedApplication(null)}
          application={selectedApplication}
          onUpdateStatus={
            selectedApplication.status === 'pending'
              ? (status) => updateApplicationStatus(selectedApplication.id, status)
              : undefined
          }
        />
      )}
    </div>
  );
}